/**
 * LandingPage — public marketing page.
 *
 * Phase E refactor (May 6, 2026): broken into composed sub-components
 * under `../components/`:
 *   - HeroSection
 *   - InstantVisualization (uses YouTubeEmbed)
 *   - FeatureGrid
 *   - SocialFooter
 *
 * Behavior, classNames, data-testids, and the auth gate (anonymous
 * Start Design / Try It Now → toast + Sign In modal; authenticated →
 * navigate to /closet-type) are byte-identical to the previous
 * monolithic LandingPage.
 */
import { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import { toast } from 'sonner';
import useStore from '@/store/useStore';
import { Button } from '@/ui/button';
import AuthHeader from '@/layout/AuthHeader';
import AuthModals from '@/features/auth/components/AuthModals';
import { useBranding } from '@/hooks/useBranding';
import { AppLogo } from '@/layout/EOCLogo';
import { FloatingWhatsApp } from '@/layout/SocialLinks';
import { withCallerOrg } from '@/utils/callerOrg';
import { API_URL } from '@/core/env';

import HeroSection from '../components/HeroSection';
import InstantVisualization from '../components/InstantVisualization';
import FeatureGrid from '../components/FeatureGrid';
import SocialFooter from '../components/SocialFooter';
import { formatWhatsAppUrl } from '../utils/whatsapp';

const LandingPage = () => {
  const setCurrentPage = useStore((s) => s.setCurrentPage);
  const currentUser = useStore((s) => s.currentUser);
  const setAuthModal = useStore((s) => s.setAuthModal);
  const setBookWithoutDesign = useStore((s) => s.setBookWithoutDesign);
  const [socialLinks, setSocialLinks] = useState({
    instagramUrl: '',
    facebookUrl: '',
    whatsappUrl: '',
    youtubeUrl: '',
    youtubePosterId: '',
  });

  useEffect(() => {
    const fetchSocialLinks = async () => {
      try {
        const response = await fetch(withCallerOrg(`${API_URL}/api/settings/social`));
        if (response.ok) {
          const data = await response.json();
          setSocialLinks(data);
        }
      } catch (error) {
        console.warn('Could not load social links');
      }
    };
    fetchSocialLinks();

    // Re-fetch when the user logs in/out so each org sees its own social + video
    const handleAuthChanged = () => fetchSocialLinks();
    window.addEventListener('authChanged', handleAuthChanged);
    return () => window.removeEventListener('authChanged', handleAuthChanged);
  }, []);

  // Use branding hook for dynamic company name and logo
  const { branding } = useBranding();

  const hasSocialLinks = socialLinks.instagramUrl || socialLinks.facebookUrl;
  const whatsappUrl = formatWhatsAppUrl(socialLinks.whatsappUrl);

  const enterDesignFlow = () => {
    // Gate: anonymous users must sign in before designing
    if (!currentUser) {
      toast.info('Please sign in to start designing');
      setAuthModal('signin');
      return;
    }
    // Set editorContext for new project design flow
    const { setEditorContext } = useStore.getState();
    setEditorContext({
      mode: 'project',
      returnTo: 'landing',
      source: 'start_design',
    });
    setCurrentPage('closet-type');
  };

  const handleStartDesign = enterDesignFlow;
  const handleTryItNow = enterDesignFlow;

  const handleBookCustomize = () => {
    setBookWithoutDesign(true);
    setCurrentPage('booking');
  };

  return (
    <div
      className="min-h-screen flex flex-col theme-bg-primary overflow-x-hidden"
      data-testid="landing-page"
    >
      {/* Header */}
      <header className="px-4 sm:px-8 py-4 sm:py-6 flex items-center justify-between border-b theme-border">
        <div className="flex items-center gap-3">
          <AppLogo className="h-10 sm:h-12 max-w-[200px] object-contain" />
          {branding.companyName && (
            <span className="hidden sm:block text-white font-bold text-lg ml-2">{branding.companyName}</span>
          )}
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Button
            onClick={handleBookCustomize}
            variant="outline"
            className="hidden sm:flex border-amber-500 text-amber-400 hover:bg-amber-500/10"
            data-testid="book-customize-header-btn"
          >
            <Calendar className="h-4 w-4 mr-2" />
            Book
          </Button>
          <AuthHeader showMyDesigns={false} />
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex flex-col">
        <HeroSection
          isAuthenticated={!!currentUser}
          onStartDesign={handleStartDesign}
          onBookCustomize={handleBookCustomize}
          onOpenMyProjects={() => setCurrentPage('saved-designs')}
        />
        <InstantVisualization
          socialLinks={socialLinks}
          onTryItNow={handleTryItNow}
        />
        <FeatureGrid />
      </main>

      <SocialFooter
        companyName={branding.companyName}
        socialLinks={socialLinks}
        hasSocialLinks={hasSocialLinks}
      />

      {/* Floating WhatsApp Button */}
      <FloatingWhatsApp url={whatsappUrl} />

      {/* Auth Modals */}
      <AuthModals />
    </div>
  );
};

export default LandingPage;
